import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { BadgeCheck, ShieldAlert, ArrowRight, Loader2, UserRound } from "lucide-react";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Public page opened by the ID-card QR. Shows who the card belongs to and whether they're active.
export default function VerifyStaff() {
    const { token: pathToken } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [token, setToken] = useState(pathToken || "");
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    // Older cards carry only the staff number (/verify?no=XXX) — resolve it to a token first
    useEffect(() => {
        const no = params.get("no");
        if (pathToken || !no) return;
        api.get(`/public/staff-by-number/${encodeURIComponent(no)}`)
            .then(({ data: d }) => navigate(`/verify/${d.token}`, { replace: true }))
            .catch(() => setError("This ID card could not be verified."));
    }, [pathToken, params, navigate]);

    useEffect(() => {
        setToken(pathToken || "");
        if (!pathToken) return;
        api.get(`/public/staff/${encodeURIComponent(pathToken)}`)
            .then(({ data: d }) => setData(d))
            .catch(() => setError("This ID card could not be verified."));
    }, [pathToken]);

    if (error) return <Shell><Invalid message={error} /></Shell>;
    if (!data) {
        return (
            <Shell>
                <div className="flex items-center gap-2 text-ink/50 text-sm py-16 justify-center" data-testid="verify-loading">
                    <Loader2 className="w-4 h-4 animate-spin" /> Checking ID card…
                </div>
            </Shell>
        );
    }
    return (
        <Shell>
            <CompanyHeader data={data} />
            <StaffCard data={data} token={token} />
            <Link
                to={`/login?audience=employee&vt=${encodeURIComponent(token)}`}
                className="mt-6 w-full inline-flex items-center justify-between bg-ink text-white px-5 py-4 text-sm font-medium hover:bg-ink/90 transition-colors"
                data-testid="verify-staff-login-link"
            >
                Staff login <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-[11px] text-ink/40 text-center" data-testid="verify-checked-at">
                Checked {new Date(data.checked_at).toLocaleString()}
            </p>
        </Shell>
    );
}

function Shell({ children }) {
    return (
        <div className="min-h-screen bg-bone flex items-start sm:items-center justify-center px-4 py-10" data-testid="verify-staff-page">
            <div className="w-full max-w-sm animate-fade-in-up">{children}</div>
        </div>
    );
}

function CompanyHeader({ data }) {
    return (
        <div className="flex items-center gap-3 mb-6" data-testid="verify-company">
            {data.company_logo ? (
                <img src={data.company_logo} alt="" className="h-10 w-10 object-contain bg-white border border-ink/10 p-1" />
            ) : (
                <div className="h-10 w-10 bg-ink text-white grid place-items-center font-display font-black">
                    {(data.company_name || "?").slice(0, 1)}
                </div>
            )}
            <div>
                <div className="font-display font-bold text-ink leading-tight" data-testid="verify-company-name">{data.company_name}</div>
                <div className="overline text-ink/40">Staff ID verification</div>
            </div>
        </div>
    );
}

function StaffCard({ data, token }) {
    const ok = data.active;
    return (
        <div className="bg-white border border-ink/10" data-testid="verify-staff-card">
            <div
                className={`px-5 py-3 flex items-center gap-2 text-sm font-semibold ${ok ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}
                data-testid="verify-status-banner"
            >
                {ok ? <BadgeCheck className="w-5 h-5" /> : <ShieldAlert className="w-5 h-5" />}
                {ok ? "Verified staff — Active" : `Not an active staff member (${data.status})`}
            </div>
            <div className="p-5 flex gap-4">
                <div className="w-24 h-28 shrink-0 bg-bone border border-ink/10 overflow-hidden grid place-items-center">
                    {data.has_photo ? (
                        <img
                            src={`${BACKEND}/api/public/staff/${encodeURIComponent(token)}/photo`}
                            alt={data.full_name}
                            className="w-full h-full object-cover"
                            data-testid="verify-photo"
                        />
                    ) : (
                        <UserRound className="w-10 h-10 text-ink/20" />
                    )}
                </div>
                <div className="min-w-0 space-y-2">
                    <div className="font-display font-black text-xl text-ink leading-tight break-words" data-testid="verify-name">{data.full_name}</div>
                    <Row label="Position" value={data.position} testid="verify-position" />
                    <Row label="Department" value={data.department} testid="verify-department" />
                    <Row label="Staff ID" value={data.employee_no} testid="verify-staff-id" mono />
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, testid, mono }) {
    return (
        <div>
            <div className="overline text-ink/40">{label}</div>
            <div className={`text-sm text-ink ${mono ? "font-mono" : ""}`} data-testid={testid}>{value || "—"}</div>
        </div>
    );
}

function Invalid({ message }) {
    return (
        <div className="bg-white border border-ink/10 p-8 text-center" data-testid="verify-invalid">
            <ShieldAlert className="w-10 h-10 text-red-600 mx-auto" />
            <div className="mt-4 font-display font-bold text-lg text-ink">Not verified</div>
            <p className="mt-2 text-sm text-ink/60">{message} Treat this card with caution and contact the issuing company.</p>
            <Link to="/login" className="mt-6 inline-block text-sm text-klein underline" data-testid="verify-invalid-login-link">Go to login</Link>
        </div>
    );
}
